// src/pages/Auth/hooks/useAuthFlow.js

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import axiosInstance from 'utils/axiosConfig';
import { FAST_API_BASE_URL, LAMBDA_BASE_URL } from 'constants';
import { useAuth } from 'context/AuthContext';
import { useSession } from './useSession';
import { 
  validateEmail, 
  validatePassword, 
  isSuperAdmin, 
  getInstallSource,
  clearAuthStorage,
  getDomainsFromResponse,
  getUserRole,
  showSnackbarMessage
} from '../utils';
import { LOADING_STATES, STORAGE_KEYS } from '../constants';

export const useAuthFlow = () => {
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const { initializeSession } = useSession();
  
  const [currentStep, setCurrentStep] = useState('email');
  const [loadingState, setLoadingState] = useState(LOADING_STATES.IDLE);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Abort controllers for cleanup
  const abortControllersRef = useRef({});

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const hideSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const resetAuthFlow = useCallback(() => {
    // Abort any ongoing requests
    Object.values(abortControllersRef.current).forEach(controller => {
      controller?.abort();
    });
    abortControllersRef.current = {};
    
    setCurrentStep('email');
    setEmail('');
    setOtp('');
    setPassword('');
    setRememberMe(false);
    setLoadingState(LOADING_STATES.IDLE);
    showSnackbar("Authentication flow reset", "info");
  }, [showSnackbar]);

  const handleSuccessfulLogin = useCallback(async (result, userEmail, isGoogle = false) => {
    const domains = getDomainsFromResponse(result);
    const userRole = getUserRole(result);
    
    if (userRole) {
      localStorage.setItem(STORAGE_KEYS.ROLE, userRole);
    }
    
    const currentRememberMe = localStorage.getItem(STORAGE_KEYS.REMEMBER_ME) === 'true';
    if (!currentRememberMe) {
      localStorage.setItem(STORAGE_KEYS.SHOW_STAY_LOGGED_IN_PROMPT, 'true');
    }
    
    const loginSuccess = login(
      {
        id: result.user_id,
        email: userEmail,
        name: result.name,
        profile_pic: result.profile_pic || null,
        is_super_admin: isSuperAdmin(result.is_super_admin),
        is_whitelabel_user: result.is_whitelabel_user,
        is_whitelabelling_reseller_only: result.is_whitelabelling_reseller_only || false,
      },
      domains
    );
    
    if (loginSuccess) {
      showSnackbar("Login Successful! Redirecting...", "success");
      
      // Navigate immediately without setTimeout
      const isSuperAdminUser = isSuperAdmin(result.is_super_admin);
      const hasDomains = domains && domains.length > 0;
      const isReadOnly = hasDomains && domains[0].role === "read only";
      
      if (isSuperAdminUser) {
        setTimeout(() => navigate("/customers"), 500);
      } else if (isReadOnly) {
        setTimeout(() => navigate("/dashboard"), 500);
      } else {
        const installSource = getInstallSource();
        if (installSource === "hubspot_marketplace") {
          localStorage.removeItem(STORAGE_KEYS.INSTALL_SOURCE);
          setTimeout(() => navigate("/integrations?tab=hubspot"), 500);
        } else {
          setTimeout(() => navigate("/settings"), 500);
        }
      }
      return true;
    }
    
    showSnackbar("Login failed. Please try again.", "error");
    return false;
  }, [login, navigate, showSnackbar]);

  const handleEmailSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateEmail(email)) {
      showSnackbar("Please enter a valid email address", "error");
      return;
    }

    setLoadingState(LOADING_STATES.EMAIL);
    
    // Create abort controller for this request
    const controller = new AbortController();
    abortControllersRef.current.email = controller;

    try {
      const response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/signup`, {
        email: email,
      }, {
        headers: {
          "X-API-KEY": process.env.REACT_APP_API_KEY,
          "Content-Type": "application/json",
        },
        signal: controller.signal
      });

      if (response.status === 200) {
        setCurrentStep('otp');
        showSnackbar("Verification code sent to your email!", "success");
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      
      console.error("Signup check failed:", error);
      
      if (error.response && error.response.status === 409) {
        setCurrentStep('password');
        // showSnackbar("Existing user found. Please enter your password.", "info");
      } else {
        showSnackbar(error.response?.data?.messages?.[0] || "Signup check failed. Please try again.", "error");
      }
    } finally {
      setLoadingState(LOADING_STATES.IDLE);
      delete abortControllersRef.current.email;
    }
  }, [email, showSnackbar]);

  const handleOtpSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length < 4) {
      showSnackbar("Please enter a valid OTP", "error");
      return;
    }

    setLoadingState(LOADING_STATES.OTP);
    
    const controller = new AbortController();
    abortControllersRef.current.otp = controller;

    try {
      const response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/verify-otp`, {
        email: email,
        otp: otp,
      }, {
        headers: {
          "X-API-KEY": process.env.REACT_APP_API_KEY,
          "Content-Type": "application/json",
        },
        signal: controller.signal
      });

      if (response.status === 200) {
        showSnackbar("Email verified successfully!", "success");
        setCurrentStep('signup');
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error("OTP verification failed:", error);
      showSnackbar(error.response?.data?.messages?.[0] || "OTP verification failed. Please try again.", "error");
    } finally {
      setLoadingState(LOADING_STATES.IDLE);
      delete abortControllersRef.current.otp;
    }
  }, [email, otp, showSnackbar]);

  const handlePasswordLogin = useCallback(async (e) => {
    e.preventDefault();
    
    if (!password) {
      showSnackbar("Please enter your password", "error");
      return;
    }

    setLoadingState(LOADING_STATES.PASSWORD);
    
    const controller = new AbortController();
    abortControllersRef.current.password = controller;

    try {
      const payload = {
        mail: email,
        password: password,
        type: "custom",
      };

      const response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/re-login`, payload, {
        signal: controller.signal
      });

      if (response.status === 200 && response.data) {
        const { result } = response.data;
        initializeSession(rememberMe);
        await handleSuccessfulLogin(result, email);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error("Password login error:", error);
      if (error.response && error.response.status === 401) {
        showSnackbar("Invalid email or password. Please try again.", "error");
      } else {
        showSnackbar(error.response?.data?.messages?.[0] || "Invalid credentials. Please try again.", "error");
      }
    } finally {
      setLoadingState(LOADING_STATES.IDLE);
      delete abortControllersRef.current.password;
    }
  }, [email, password, rememberMe, handleSuccessfulLogin, initializeSession, showSnackbar]);

  const handleForgotPassword = useCallback(async (forgotEmail) => {
    if (!validateEmail(forgotEmail)) {
      showSnackbar("Please enter a valid email address", "error");
      return { success: false, message: "Invalid email" };
    }

    setLoadingState(LOADING_STATES.FORGOT_PASSWORD);

    try {
      const response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/reset-password`, {
        email: forgotEmail,
      });

      if (response && response.status === 200) {
        showSnackbar("Reset link sent to your email!", "success");
        return { success: true, message: "Reset link sent successfully" };
      } else {
        showSnackbar("Failed to send reset link. Please try again.", "error");
        return { success: false, message: "Failed to send reset link" };
      }
    } catch (err) {
      console.error("Send reset failed:", err);
      const errorMessage = err.response?.data?.messages?.[0] || "Failed to send reset link. Email doesn't exist.";
      showSnackbar(errorMessage, "error");
      return { success: false, message: errorMessage };
    } finally {
      setLoadingState(LOADING_STATES.IDLE);
    }
  }, [showSnackbar]);

  const handleGoogleSignIn = useCallback(async (credentialResponse) => {
    setLoadingState(LOADING_STATES.GOOGLE);
    
    const controller = new AbortController();
    abortControllersRef.current.google = controller;

    try {
      const decoded = jwtDecode(credentialResponse.credential);
      
      // First try to login
      let response;
      try {
        response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/re-login`, {
          mail: decoded.email,
          name: decoded.name,
          profile_pic: decoded.picture,
          type: "google",
        }, { signal: controller.signal });
      } catch (reLoginError) {
        // If user doesn't exist, create account
        if (reLoginError.response && reLoginError.response.status === 401) {
          showSnackbar("Creating new account...", "info");
          
          const saveUserResponse = await axiosInstance.post(
            `${FAST_API_BASE_URL}/api/save-google-user`,
            {
              email: decoded.email,
              name: decoded.name,
              profile_pic: decoded.picture,
              referral_code: localStorage.getItem(STORAGE_KEYS.REFERRAL_CODE) || null
            },
            {
              headers: {
                "X-API-KEY": process.env.REACT_APP_API_KEY,
                "Content-Type": "application/json",
              },
              signal: controller.signal
            }
          );

          if (saveUserResponse.status === 200) {
            showSnackbar("Account created successfully!", "success");
            response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/re-login`, {
              mail: decoded.email,
              name: decoded.name,
              profile_pic: decoded.picture,
              type: "google",
            }, { signal: controller.signal });
          }
        } else {
          throw reLoginError;
        }
      }

      if (response && response.status === 200 && response.data) {
        const { result } = response.data;
        const domains = getDomainsFromResponse(result);
        
        initializeSession(false);
        
        if (domains.length === 0) {
          // No domain - need to register
          setCurrentStep('registration');
          setEmail(decoded.email);
          showSnackbar("Please register your domain to continue.", "info");
        } else {
          await handleSuccessfulLogin(result, decoded.email, true);
        }
        
        localStorage.removeItem(STORAGE_KEYS.REFERRAL_CODE);
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error("Google login error:", error);
      showSnackbar("Failed to login with Google. Please try again.", "error");
    } finally {
      setLoadingState(LOADING_STATES.IDLE);
      delete abortControllersRef.current.google;
    }
  }, [handleSuccessfulLogin, initializeSession, showSnackbar]);

  const handleSignupSubmit = useCallback(async (signupData) => {
    setLoadingState(LOADING_STATES.SIGNUP);

    try {
      const payload = {
        password: signupData.password,
        email: email,
        user_name: `${signupData.firstName} ${signupData.lastName}`.trim(),
        role: "admin",
        referral_code: localStorage.getItem(STORAGE_KEYS.REFERRAL_CODE) || null
      };

      const response = await axiosInstance.post(
        `${FAST_API_BASE_URL}/api/save-password`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        showSnackbar("Account created successfully!", "success");
        localStorage.removeItem(STORAGE_KEYS.REFERRAL_CODE);
        
        const loginResponse = await axiosInstance.post(`${FAST_API_BASE_URL}/api/user/re-login`, {
          mail: email,
          password: signupData.password,
          type: "custom",
        });

        if (loginResponse.status === 200) {
          const { result } = loginResponse.data;
          const domains = getDomainsFromResponse(result);
          const userRole = getUserRole(result);
          
          if (userRole) {
            localStorage.setItem(STORAGE_KEYS.ROLE, userRole);
          }
          
          initializeSession(false);
          
          login(
            {
              id: result.user_id,
              email: email,
              name: `${signupData.firstName} ${signupData.lastName}`.trim(),
              is_super_admin: isSuperAdmin(result.is_super_admin),
              is_whitelabel_user: result.is_whitelabel_user
            },
            domains
          );
          
          setCurrentStep('registration');
          showSnackbar("Please complete domain registration", "info");
          return { success: true };
        }
      }
    } catch (err) {
      console.error("Final signup failed:", err);
      showSnackbar("Failed to create account! Please try again.", "error");
      return { success: false, error: err };
    } finally {
      setLoadingState(LOADING_STATES.IDLE);
    }
  }, [email, login, initializeSession, showSnackbar]);

  const handleRegistrationSubmit = useCallback(async (registrationData) => {
    setLoadingState(LOADING_STATES.REGISTRATION);

    try {
      const cleanDomain = registrationData.cleanDomain;
      const payload1 = {
        domain_url: cleanDomain,
        source: registrationData.websitePlatform,
        email: email,
        first_name: user?.name?.split(' ')[0] || '',
        last_name: user?.name?.split(' ')[1] || '',
      };

      const payload = {
        domain: cleanDomain,
        source: registrationData.websitePlatform,
        company_name: registrationData.companyName,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        date_formate: "MM-DD-YYYY",
        email: email,
        user_name: user?.name || '',
        is_google_user: false,
        is_user_logged_in: false
      };

      await axiosInstance.post(`${LAMBDA_BASE_URL}/save-registration`, payload1, {
        headers: {
          "X-API-KEY": process.env.REACT_APP_API_KEY,
          "Content-Type": "application/json",
        },
      });

      if (registrationData.websitePlatform === "wordpress" || registrationData.websitePlatform === "webflow") {
        showSnackbar("Thank you for registering with Ansera! Redirecting to plugin...", "success");
        localStorage.setItem('justRedirected', 'true');
        setTimeout(() => {
          if (registrationData.websitePlatform === "wordpress") {
            window.location.href = `https://wordpress.org/plugins/ansera-search`;
          } else if (registrationData.websitePlatform === "webflow") {
            window.location.href = "https://webflow.com/apps/detail/ansera-search";
          }
        }, 3000);
      } else if (["wix", "squarespace", "custom"].includes(registrationData.websitePlatform)) {
        const response = await axiosInstance.post(`${FAST_API_BASE_URL}/api/register`, payload, {
          headers: {
            "X-API-KEY": process.env.REACT_APP_API_KEY,
            "Content-Type": "application/json",
          },
        });

        if (response.status === 200) {
          showSnackbar("Registration successful! Redirecting...", "success");
          const installSource = getInstallSource();
          localStorage.setItem("role","admin")
          setTimeout(() => {
            if (installSource === "hubspot_marketplace") {
              localStorage.removeItem(STORAGE_KEYS.INSTALL_SOURCE);
              navigate('/integrations?tab=hubspot');
            } else {
              navigate('/settings');
            }
          }, 2000);
        }
      }
      
      return { success: true };
    } catch (err) {
      console.error("Domain registration failed:", err);
      showSnackbar("Domain registration failed. Please try again.", "error");
      return { success: false, error: err };
    } finally {
      setLoadingState(LOADING_STATES.IDLE);
    }
  }, [email, user, navigate, showSnackbar]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(abortControllersRef.current).forEach(controller => {
        controller?.abort();
      });
    };
  }, []);

  return {
    currentStep,
    setCurrentStep,
    loadingState,
    email,
    setEmail,
    otp,
    setOtp,
    password,
    setPassword,
    rememberMe,
    setRememberMe,
    snackbar,
    showSnackbar,
    hideSnackbar,
    resetAuthFlow,
    handleEmailSubmit,
    handleOtpSubmit,
    handlePasswordLogin,
    handleForgotPassword,
    handleGoogleSignIn,
    handleSignupSubmit,
    handleRegistrationSubmit,
    isAuthenticated: !!user,
    userEmail: user?.email || "",
    userName: user?.name || ""
  };
};